import { Flex, RadioCard } from '@chakra-ui/react';
import * as React from 'react';

interface RadioCardItemProps extends RadioCard.ItemProps {
  icon?: React.ReactElement;
  /**
   * Where the icon sits relative to the label/description stack. `top` keeps it
   * inside that stack (the historical layout); `start` moves it to the row's
   * leading edge so a square tile can ride beside the text — the shape used by
   * option lists that lead with a pictogram.
   */
  iconPlacement?: 'top' | 'start';
  label?: React.ReactNode;
  description?: React.ReactNode;
  addon?: React.ReactNode;
  indicator?: React.ReactNode | null;
  indicatorPlacement?: 'start' | 'end' | 'inside';
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  /**
   * Styles for the card's inner control — the flex row holding icon, label and
   * description. Chakra renders it without a `data-part`, so a consumer cannot
   * reach it from the item's own `css` prop; pass its styles here instead.
   */
  controlProps?: RadioCard.ItemControlProps;
  /**
   * Optional element to display right-aligned in the card (e.g. a badge)
   */
  rightElement?: React.ReactNode;
}

export const RadioCardItem = React.forwardRef<HTMLInputElement, RadioCardItemProps>(
  function RadioCardItem(props, ref) {
    const {
      inputProps,
      label,
      description,
      addon,
      icon,
      iconPlacement = 'top',
      indicator = <RadioCard.ItemIndicator />,
      indicatorPlacement = 'end',
      controlProps,
      rightElement,
      ...rest
    } = props;

    const hasContent = label || description || icon;
    const leadingIcon = iconPlacement === 'start' ? icon : undefined;
    // A leading icon needs the text to stack beside it, so the content slot is
    // required even when the indicator is suppressed.
    const ContentWrapper = indicator || leadingIcon ? RadioCard.ItemContent : React.Fragment;

    return (
      <RadioCard.Item rounded="lg" {...rest}>
        <RadioCard.ItemHiddenInput ref={ref} {...inputProps} />
        <RadioCard.ItemControl {...controlProps}>
          {indicatorPlacement === 'start' && indicator}
          {hasContent && (
            <>
              {leadingIcon}
              <ContentWrapper>
                {!leadingIcon && icon}
                {label && <RadioCard.ItemText fontWeight="medium">{label}</RadioCard.ItemText>}
                {description && (
                  <RadioCard.ItemDescription>{description}</RadioCard.ItemDescription>
                )}
                {indicatorPlacement === 'inside' && indicator}
              </ContentWrapper>
            </>
          )}
          {/* Trailing content rides the row's end: the content slot already
              claims the free space, so no auto margin is needed. */}
          {rightElement && (
            <Flex flex="none" align="center">
              {rightElement}
            </Flex>
          )}
          {indicatorPlacement === 'end' && indicator}
        </RadioCard.ItemControl>
        {addon && <RadioCard.ItemAddon>{addon}</RadioCard.ItemAddon>}
      </RadioCard.Item>
    );
  }
);

export const RadioCardRoot = RadioCard.Root;
export const RadioCardLabel = RadioCard.Label;
export const RadioCardItemIndicator = RadioCard.ItemIndicator;
